<script>
  import { token } from "./store/token.js";
  import { permissions } from "./store/permissions.js";

  let email;
  let password;
  let error_message = null;
  let isLoading = false;

  const handle_login = async () => {
    isLoading = true;
    error_message = null;
    
    try {
      const login_response = await fetch(
        `${process.env.API_ROOT}/admin/login`,
        {
          method: "post",
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ email, password })
        }
      );

      const response = await login_response.json();

      if (response.code) {
        error_message = response.message;
        return;
      }

      // Store permissions from new response format (include email from response or form)
      permissions.set({
        role: response.role || 'admin',
        channels: response.channels || [],
        email: response.email ?? email,
        pages: response.pages || {
          workspace_info: true,
          user_list: true,
          analytics: true,
          upgrades: true,
          promo_codes: true
        }
      });

      token.set(response.token);
    } catch (e) {
      error_message = "An unexpected error occurred. Please try again.";
      console.error(e);
    } finally {
      isLoading = false;
    }
  };
</script>

<style>
  .login-container {
    min-height: 100vh;
    background: linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem;
  }

  .login-card {
    background: var(--ds-surface, #fff);
    border-radius: var(--ds-radius-lg, 16px);
    box-shadow: var(--ds-shadow-card, 0 2px 8px rgba(0, 0, 0, 0.04));
    width: 100%;
    max-width: 420px;
    padding: 2.5rem;
  }

  .logo {
    width: 180px;
    margin-bottom: 2.5rem;
    transition: transform 0.2s ease;
  }

  .logo:hover {
    transform: scale(1.02);
  }

  .form-group {
    margin-bottom: 1.5rem;
    text-align: left;
  }

  .form-label {
    display: block;
    margin-bottom: 0.5rem;
    color: var(--ds-text-muted, #6b7280);
    font-size: 0.9rem;
    font-weight: 500;
  }

  .form-input {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 2px solid #e2e8f0;
    border-radius: 8px;
    font-size: 1rem;
    transition: all 0.2s ease;
    background: #f8fafc;
  }

  .form-input:focus {
    outline: none;
    border-color: var(--ds-accent, #2563eb);
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
    background: white;
  }

  .login-button {
    width: 100%;
    padding: 0.875rem;
    background: var(--ds-accent, #2563eb);
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    position: relative;
  }

  .login-button:hover {
    background: var(--ds-accent-hover, #1d4ed8);
    transform: translateY(-1px);
  }

  .login-button:active {
    transform: translateY(0);
  }

  .login-button:disabled {
    background: #a0aec0;
    cursor: not-allowed;
    transform: none;
  }

  .error-message {
    background: #fff5f5;
    color: #c53030;
    padding: 1rem;
    border-radius: 8px;
    margin-bottom: 1.5rem;
    font-size: 0.9rem;
    border: 1px solid #feb2b2;
  }

  .loading-spinner {
    display: inline-block;
    width: 1.25rem;
    height: 1.25rem;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    border-top-color: white;
    animation: spin 0.8s linear infinite;
    margin-right: 0.5rem;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  @media (max-width: 480px) {
    .login-card {
      padding: 1.5rem;
    }
  }
</style>

<div class="login-container">
  <img
    src="/images/vollume-logo-black.png"
    class="logo"
    alt="Vollume Logo" />

  <div class="login-card">
    <h1 class="h4 text-center mb-4" style="color: var(--ds-text, #111827);">Welcome Back</h1>

    {#if error_message}
      <div class="error-message" role="alert">
        {error_message}
      </div>
    {/if}

    <div class="form-group">
      <label class="form-label" for="email">Email</label>
      <input
        id="email"
        bind:value={email}
        type="email"
        class="form-input"
        placeholder="Enter your email"
        aria-label="Email"
        disabled={isLoading} />
    </div>

    <div class="form-group">
      <label class="form-label" for="password">Password</label>
      <input
        id="password"
        bind:value={password}
        type="password"
        class="form-input"
        placeholder="Enter your password"
        aria-label="Password"
        disabled={isLoading} />
    </div>

    <button
      on:click={handle_login}
      class="login-button"
      disabled={isLoading}>
      {#if isLoading}
        <span class="loading-spinner"></span>
        Signing in...
      {:else}
        Sign In
      {/if}
    </button>
  </div>
</div>
